import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  numeric,
  index,
  uniqueIndex,
  check,
  primaryKey,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { pages, pageVersions } from "./page-builder";
import { sites } from "./page-builder-sites";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Blog status enum
// ---------------------------------------------------------------------------

export const BLOG_POST_STATUSES = [
  "draft",
  "published",
  "scheduled",
  "archived",
] as const;

export type BlogPostStatus = (typeof BLOG_POST_STATUSES)[number];

export const AB_STATUSES = [
  "draft",
  "running",
  "paused",
  "concluded",
] as const;

export type AbStatus = (typeof AB_STATUSES)[number];

// ---------------------------------------------------------------------------
// Blog series
// ---------------------------------------------------------------------------

export const blogSeries = pgTable(
  "blog_series",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    slug: text("slug").notNull(),
    description: text("description"),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_blog_series_site_slug").on(table.siteId, table.slug),
    index("idx_blog_series_tenant").on(table.tenantId),
    index("idx_blog_series_site").on(table.siteId),
  ],
);

// ---------------------------------------------------------------------------
// Blog posts
// ---------------------------------------------------------------------------

export const blogPosts = pgTable(
  "blog_posts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    slug: text("slug").notNull(),
    title: text("title").notNull(),
    excerpt: text("excerpt"),
    featuredImage: text("featured_image"),
    authorId: text("author_id").notNull(),
    authorName: text("author_name").notNull(),
    status: text("status").notNull().default("draft"),
    publishedAt: timestamp("published_at", tz),
    scheduledFor: timestamp("scheduled_for", tz),
    readingTimeMin: integer("reading_time_min"),
    seoMeta: jsonb("seo_meta")
      .$type<Record<string, unknown>>()
      .default({}),
    structuredData: jsonb("structured_data")
      .$type<Record<string, unknown>>()
      .default({}),
    crmStageTags: jsonb("crm_stage_tags")
      .$type<string[]>()
      .notNull()
      .default([]),
    canonicalUrl: text("canonical_url"),
    allowComments: boolean("allow_comments").notNull().default(true),
    commentCount: integer("comment_count").notNull().default(0),
    featured: boolean("featured").notNull().default(false),
    seriesId: uuid("series_id").references(() => blogSeries.id, { onDelete: "set null" }),
    seriesOrder: integer("series_order"),
    seoScore: integer("seo_score"),
    language: text("language").notNull().default("en"),
    sendToSubscribersOnPublish: boolean("send_to_subscribers_on_publish").notNull().default(true),
    newsletterSent: boolean("newsletter_sent").notNull().default(false),
    createSocialDraftOnPublish: boolean("create_social_draft_on_publish").notNull().default(false),
    socialDraftPostId: uuid("social_draft_post_id"),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_blog_posts_site_slug").on(table.siteId, table.slug),
    index("idx_blog_posts_tenant").on(table.tenantId),
    index("idx_blog_posts_site_status").on(table.siteId, table.status),
    index("idx_blog_posts_published").on(table.siteId, table.publishedAt),
    index("idx_blog_posts_featured").on(table.siteId, table.featured),
    check(
      "chk_blog_post_status",
      sql`${table.status} IN ('draft', 'published', 'scheduled', 'archived')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Blog categories + tags
// ---------------------------------------------------------------------------

export const blogCategories = pgTable(
  "blog_categories",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description"),
    parentId: uuid("parent_id"),
    sortOrder: integer("sort_order").notNull().default(0),
  },
  (table) => [
    uniqueIndex("idx_blog_cat_site_slug").on(table.siteId, table.slug),
    index("idx_blog_cat_parent").on(table.parentId),
  ],
);

export const blogTags = pgTable(
  "blog_tags",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
  },
  (table) => [
    uniqueIndex("idx_blog_tag_site_slug").on(table.siteId, table.slug),
  ],
);

export const blogPostCategories = pgTable(
  "blog_post_categories",
  {
    postId: uuid("post_id")
      .notNull()
      .references(() => blogPosts.id, { onDelete: "cascade" }),
    categoryId: uuid("category_id")
      .notNull()
      .references(() => blogCategories.id, { onDelete: "cascade" }),
  },
  (table) => [primaryKey({ columns: [table.postId, table.categoryId] })],
);

export const blogPostTags = pgTable(
  "blog_post_tags",
  {
    postId: uuid("post_id")
      .notNull()
      .references(() => blogPosts.id, { onDelete: "cascade" }),
    tagId: uuid("tag_id")
      .notNull()
      .references(() => blogTags.id, { onDelete: "cascade" }),
  },
  (table) => [primaryKey({ columns: [table.postId, table.tagId] })],
);

// ---------------------------------------------------------------------------
// A/B experiments
// ---------------------------------------------------------------------------

export const abExperiments = pgTable(
  "ab_experiments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    status: text("status").notNull().default("draft"),
    trafficSplit: jsonb("traffic_split")
      .$type<Record<string, number>>()
      .default({}),
    winnerId: text("winner_id"),
    concludedAt: timestamp("concluded_at", tz),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_ab_experiments_page").on(table.pageId),
    index("idx_ab_experiments_tenant_status").on(
      table.tenantId,
      table.status,
    ),
    check(
      "chk_ab_status",
      sql`${table.status} IN ('draft', 'running', 'paused', 'concluded')`,
    ),
  ],
);

export const abVariants = pgTable(
  "ab_variants",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    experimentId: uuid("experiment_id")
      .notNull()
      .references(() => abExperiments.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    versionId: uuid("version_id").references(() => pageVersions.id, {
      onDelete: "set null",
    }),
    r2Key: text("r2_key"),
    isControl: boolean("is_control").notNull().default(false),
    impressions: integer("impressions").notNull().default(0),
    conversions: integer("conversions").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_ab_variants_experiment").on(table.experimentId),
  ],
);

// ---------------------------------------------------------------------------
// Lighthouse audit results
// ---------------------------------------------------------------------------

export const pageAuditResults = pgTable(
  "page_audit_results",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    versionId: uuid("version_id").references(() => pageVersions.id, {
      onDelete: "set null",
    }),
    auditUrl: text("audit_url").notNull(),
    performanceScore: integer("performance_score"),
    accessibilityScore: integer("accessibility_score"),
    seoScore: integer("seo_score"),
    bestPracticesScore: integer("best_practices_score"),
    lcpMs: integer("lcp_ms"),
    inpMs: integer("inp_ms"),
    cls: numeric("cls", { precision: 5, scale: 4 }),
    fcpMs: integer("fcp_ms"),
    ttfbMs: integer("ttfb_ms"),
    rawReport: jsonb("raw_report"),
    auditedAt: timestamp("audited_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_page_audits_page").on(table.pageId, table.auditedAt),
    index("idx_page_audits_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// Blog comments (C81 — threaded, moderated)
// ---------------------------------------------------------------------------

export const COMMENT_STATUSES = [
  "pending",
  "approved",
  "spam",
  "deleted",
] as const;

export type CommentStatus = (typeof COMMENT_STATUSES)[number];

export const blogComments = pgTable(
  "blog_comments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    postId: uuid("post_id")
      .notNull()
      .references(() => blogPosts.id, { onDelete: "cascade" }),
    blockId: text("block_id"),
    parentId: uuid("parent_id"),
    authorName: text("author_name").notNull(),
    // No email field — authenticated users: look up via authorUserId → auth table. Anonymous: no email collected (data minimisation).
    authorUserId: text("author_user_id"),
    content: text("content").notNull(),
    aiClassification: text("ai_classification"),
    aiConfidence: numeric("ai_confidence", { precision: 3, scale: 2 }),
    aiRationale: text("ai_rationale"),
    status: text("status").notNull().default("pending"),
    ipHash: text("ip_hash"),
    likesCount: integer("likes_count").notNull().default(0),
    isPinned: boolean("is_pinned").notNull().default(false),
    moderatedBy: text("moderated_by"),
    moderatedAt: timestamp("moderated_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_blog_comments_post").on(table.postId, table.status),
    index("idx_blog_comments_post_block").on(table.postId, table.blockId, table.status),
    index("idx_blog_comments_tenant").on(table.tenantId),
    index("idx_blog_comments_parent").on(table.parentId),
    check(
      "chk_comment_status",
      sql`${table.status} IN ('pending', 'approved', 'spam', 'deleted')`,
    ),
    check(
      "chk_comment_ai_classification",
      sql`${table.aiClassification} IS NULL OR ${table.aiClassification} IN ('spam', 'constructive', 'question', 'complaint', 'purchase_intent')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Blog subscriptions (C81 — double opt-in, encrypted email)
// ---------------------------------------------------------------------------

export const blogSubscriptions = pgTable(
  "blog_subscriptions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    encryptedEmail: text("encrypted_email").notNull(), // Always encrypted (enc:v1: prefix). Decrypt only for sending.
    emailHash: text("email_hash").notNull(), // HMAC-SHA-256 for lookup/dedup without decrypting
    confirmed: boolean("confirmed").notNull().default(false),
    confirmToken: text("confirm_token"),
    categoryPreferences: jsonb("category_preferences")
      .$type<string[]>()
      .default([]),
    unsubscribedAt: timestamp("unsubscribed_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_blog_sub_site_email").on(table.siteId, table.emailHash),
    index("idx_blog_sub_tenant").on(table.tenantId),
    index("idx_blog_sub_confirmed").on(table.siteId, table.confirmed),
  ],
);

// ---------------------------------------------------------------------------
// Funnel sessions (cross-step tracking)
// ---------------------------------------------------------------------------

export const funnelSessions = pgTable(
  "funnel_sessions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id").notNull(),
    visitorId: text("visitor_id").notNull(),
    entryStepId: uuid("entry_step_id").notNull(),
    currentStepId: uuid("current_step_id"),
    completed: boolean("completed").notNull().default(false),
    utmSource: text("utm_source"),
    utmMedium: text("utm_medium"),
    utmCampaign: text("utm_campaign"),
    experimentId: uuid("experiment_id"),
    variantId: uuid("variant_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_funnel_sessions_tenant_site_visitor").on(
      table.tenantId,
      table.siteId,
      table.visitorId,
    ),
    index("idx_funnel_sessions_tenant").on(table.tenantId),
    index("idx_funnel_sessions_created").on(table.createdAt),
  ],
);
